import * as productApi from './apiClient'
export * from './apiClient'
import type {
  ChangeItemCodeInput,
  ItemCodeHistory,
  PagedResult,
  Product,
  ProductInput,
  ProductListParams,
  ProductUpdateInput,
} from './types'
import {
  ensureWorkspaceForProduct,
  migrateWorkspaceProductCode,
} from '@/services/workspace/mockRepository'

/**
 * Server-side filter/sort/page over API list.
 */
async function listFiltered(
  params: ProductListParams,
): Promise<PagedResult<Product>> {
  return productApi.listProductsApi(params)
}

export const productService = {
  list(params: ProductListParams = {}): Promise<PagedResult<Product>> {
    return listFiltered(params)
  },
  getByCode(code: string): Promise<Product | null> {
    return productApi.getProductByCodeApi(code)
  },
  getById(id: string): Promise<Product | null> {
    return productApi.getProductApi(id)
  },
  async create(input: ProductInput): Promise<Product> {
    const created = await productApi.createProductApi(input)
    // Workspace still keyed by item_code (DECISIONS.md gap — not migrated).
    ensureWorkspaceForProduct(created.code)
    return created
  },
  update(id: string, input: ProductUpdateInput): Promise<Product> {
    return productApi.updateProductApi(id, input)
  },
  archive(id: string): Promise<Product> {
    return productApi.archiveProductApi(id, 'inactive')
  },
  async changeItemCode(
    id: string,
    input: ChangeItemCodeInput,
  ): Promise<Product> {
    const current = await productApi.getProductApi(id)
    if (!current) {
      throw new productApi.ProductsApiClientError(
        'NOT_FOUND',
        'Không tìm thấy mã hàng.',
        404,
      )
    }
    const updated = await productApi.changeProductCodeApi(id, input)
    migrateWorkspaceProductCode(current.code, updated.code)
    return updated
  },
  /**
   * GAP: no GET item_code_history API yet — returns [] (not invented rows).
   * History row is only returned inline on change-code response (not persisted client-side).
   */
  listCodeHistory(_productId: string): Promise<ItemCodeHistory[]> {
    return Promise.resolve([])
  },
}

export type {
  ChangeItemCodeInput,
  ItemCodeHistory,
  PagedResult,
  Product,
  ProductInput,
  ProductListParams,
  ProductStatus,
  ProductUpdateInput,
} from './types'
export {
  ARCHIVED_STATUSES,
  formatAttributeNames,
  formatDimension,
  formatDimensionInch,
  inchToMm,
  isArchivedStatus,
  PRODUCT_STATUS_OPTIONS,
  roundMmUp,
  statusLabel,
} from './types'
export { ProductsApiClientError } from './apiClient'
